import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { AdminShell } from '../../components/AdminShell';
import { formatDate } from '../../lib/format';

interface AdminWorkspace {
  id: string;
  name: string;
  inbox_alias: string;
  company_id: string;
  company_name: string;
  country_code: string;
  verification_status: string;
  plan: string;
  member_count: number;
  invoice_count: number;
  created_at: string;
}

/** Admin → Workspaces. Read-only inventory across companies. */
export function AdminWorkspacesPage() {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState('');
  const rows = useQuery<AdminWorkspace[]>({
    queryKey: ['admin-workspaces', q],
    queryFn: async () => (await api.get('/admin/workspaces', { params: { q: q || undefined } })).data,
  });

  return (
    <AdminShell>
      <div className="px-8 py-7 max-w-7xl w-full mx-auto">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500">{t('admin.eyebrow')}</div>
            <h1 className="display text-3xl font-semibold tracking-tight mt-1">{t('admin.workspaces.title')}</h1>
            <p className="text-sm text-ink-600 dark:text-ink-300 mt-1">{t('admin.workspaces.subtitle')}</p>
          </div>
          <div className="relative w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('admin.workspaces.searchPlaceholder')}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900"
            />
          </div>
        </div>

        <Card className="mt-6 overflow-hidden">
          {rows.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !rows.data || rows.data.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('admin.workspaces.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 dark:bg-ink-800/50 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium">{t('admin.workspaces.col.workspace')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.workspaces.col.company')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.workspaces.col.plan')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.workspaces.col.country')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('admin.workspaces.col.members')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('admin.workspaces.col.invoices')}</th>
                    <th className="px-4 py-2 font-medium">{t('admin.workspaces.col.created')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.data.map((w) => (
                    <tr key={w.id} className="border-t border-ink-200 dark:border-ink-800">
                      <td className="px-4 py-3">
                        <div className="font-medium">{w.name}</div>
                        <div className="text-xs text-ink-500 font-mono">{w.inbox_alias}@in.plyez.com</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-ink-700 dark:text-ink-200">{w.company_name}</div>
                        <div className="text-xs text-ink-500">
                          <Pill tone={w.verification_status === 'verified' ? 'success' : 'accent'}>
                            {w.verification_status}
                          </Pill>
                        </div>
                      </td>
                      <td className="px-4 py-3"><Pill tone="muted">{w.plan}</Pill></td>
                      <td className="px-4 py-3 text-xs font-mono">{w.country_code}</td>
                      <td className="px-4 py-3 num text-right">{w.member_count}</td>
                      <td className="px-4 py-3 num text-right">{w.invoice_count}</td>
                      <td className="px-4 py-3 num text-ink-500 text-xs">{formatDate(w.created_at, i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </AdminShell>
  );
}
